/**
 * FounderContext.jsx — Founder Mode State
 * 
 * Founder exists ABOVE access control.
 * Founder access is absolute, unconditional, non-revocable.
 * 
 * Activation methods:
 * 1. Environment variable: REACT_APP_FOUNDER_MODE=true
 * 2. localStorage: granullo_founder_key (set via /founder route)
 * 3. Query param: ?founder=true (dev/staging only)
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const FounderContext = createContext({
  isFounder: false,
  founderOverride: false,
  viewMode: 'public', // 'public' | 'demo' | 'founder'
  setFounderOverride: () => {},
  setViewMode: () => {},
  activateFounder: () => {},
  deactivateFounder: () => {},
});

const FOUNDER_STORAGE_KEY = 'granullo_founder_key';
const FOUNDER_SECRET = 'granullo_founder_2026';

export const FounderProvider = ({ children }) => {
  const [isFounder, setIsFounder] = useState(false);
  const [founderOverride, setFounderOverride] = useState(false);
  const [viewMode, setViewMode] = useState('public');
  
  useEffect(() => {
    // Check activation methods
    const checkFounderStatus = () => {
      // 1. Environment variable
      if (process.env.REACT_APP_FOUNDER_MODE === 'true') {
        return true;
      }
      
      // 2. localStorage key
      const storedKey = localStorage.getItem(FOUNDER_STORAGE_KEY);
      if (storedKey === FOUNDER_SECRET) {
        return true;
      }
      
      // 3. Query param (dev only)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('founder') === 'true') {
        return true;
      }
      
      return false;
    };
    
    setIsFounder(checkFounderStatus());
  }, []);
  
  const activateFounder = (key) => {
    if (key === FOUNDER_SECRET) {
      localStorage.setItem(FOUNDER_STORAGE_KEY, FOUNDER_SECRET);
      setIsFounder(true);
      setFounderOverride(true);
      setViewMode('founder');
      return true;
    }
    return false;
  };
  
  const deactivateFounder = () => {
    localStorage.removeItem(FOUNDER_STORAGE_KEY);
    setIsFounder(false);
    setFounderOverride(false);
    setViewMode('public');
  };
  
  return (
    <FounderContext.Provider value={{
      isFounder,
      founderOverride,
      viewMode,
      setFounderOverride,
      setViewMode,
      activateFounder,
      deactivateFounder,
    }}>
      {children}
    </FounderContext.Provider>
  );
};

export const useFounder = () => useContext(FounderContext);

export default FounderContext;
